import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// Connects to data-controller="direct-upload"
// Reusable direct-upload field: shows progress, disables submit until done, stores signed_id in form.
export default class extends Controller {
  static targets = ["input", "submit", "progress", "progressBar", "progressText", "debug"]

  connect() {
    this.abortController = null
    this.submitButton = this.findSubmitButton()
    this.isUploading = false
    this.hideProgress()
    if (this.hasDebugTarget) this.debug("DirectUpload controller connected")
  }

  findSubmitButton() {
    if (this.hasSubmitTarget) return this.submitTarget
    const form = this.element.closest("form")
    return form?.querySelector("input[type='submit'], button[type='submit']") ?? null
  }

  uploadStarted(event) {
    if (event.type !== "change") {
      this.isUploading = true
      this.disableSubmitButton()
      this.showProgress()
      this.updateProgress(0)
      return
    }

    const input = event.target
    const file = input.files?.[0]
    if (!file) {
      this.isUploading = false
      this.enableSubmitButton()
      this.hideProgress()
      return
    }

    if (this.hasDebugTarget) this.debug(`File selected: ${file.name} (${this.formatBytes(file.size)})`)

    this.isUploading = true
    this.disableSubmitButton()
    this.showProgress()
    this.updateProgress(0)

    const url = this.element.dataset.directUploadUrlValue || this.element.dataset.directUploadUrl
    const uploadUrl = url.startsWith("http") ? url : new URL(url, window.location.origin).href
    const upload = new DirectUpload(file, uploadUrl, this)

    upload.create((error, blob) => {
      if (error) {
        if (this.hasDebugTarget) this.debug(`Upload failed: ${error}`, "error")
        this.showError()
        this.isUploading = false
        this.enableSubmitButton()
        return
      }

      if (this.hasDebugTarget) this.debug(`Upload success. Signed id: ${blob.signed_id}`)

      const form = input.closest("form")
      const hidden = document.createElement("input")
      hidden.type = "hidden"
      hidden.name = input.name
      hidden.value = blob.signed_id
      form.appendChild(hidden)
      input.removeAttribute("name")

      this.updateProgress(100)
      this.showSuccess()
      this.isUploading = false
      this.enableSubmitButton()
    })
  }

  uploadProgressing(event) {
    const pct = Math.round((event.detail?.progress ?? 0) * 100)
    this.updateProgress(pct)
  }

  uploadFinished(event) {
    this.isUploading = false
    this.enableSubmitButton()
    this.updateProgress(100)
    this.showSuccess()
  }

  uploadFailed(event) {
    this.isUploading = false
    this.enableSubmitButton()
    this.showError()
    if (this.hasDebugTarget) this.debug(`Upload failed: ${event.detail?.error ?? "Unknown"}`, "error")
  }

  disableSubmitButton() {
    if (this.submitButton) this.submitButton.disabled = true
  }

  enableSubmitButton() {
    if (!this.submitButton) return
    const form = this.element.closest("form")
    if (!form) {
      this.submitButton.disabled = false
      return
    }
    const others = form.querySelectorAll("[data-controller*='direct-upload']")
    let anyUploading = false
    others.forEach((el) => {
      if (el === this.element) return
      const ctrl = this.application.getControllerForElementAndIdentifier(el, "direct-upload")
      if (ctrl?.isUploading) anyUploading = true
    })
    if (!anyUploading) this.submitButton.disabled = false
  }

  showProgress() {
    if (this.hasProgressTarget) this.progressTarget.style.display = "block"
    if (this.hasProgressTextTarget) this.progressTextTarget.style.display = "block"
  }

  hideProgress() {
    if (this.hasProgressTarget) this.progressTarget.style.display = "none"
    if (this.hasProgressTextTarget) this.progressTextTarget.style.display = "none"
  }

  updateProgress(percentage) {
    const bar = this.hasProgressBarTarget ? this.progressBarTarget : this.progressTarget?.querySelector("[data-direct-upload-target='progressBar']")
    if (bar) {
      bar.style.width = `${percentage}%`
      bar.setAttribute("aria-valuenow", percentage)
    }
    if (this.hasProgressTextTarget) {
      const span = this.progressTextTarget.querySelector(".progress-percentage")
      if (span) span.textContent = `${percentage}%`
    }
  }

  showSuccess() {
    const bar = this.hasProgressBarTarget ? this.progressBarTarget : this.progressTarget?.querySelector("[data-direct-upload-target='progressBar']")
    if (bar) {
      bar.classList.remove("bg-error")
      bar.classList.add("bg-success")
    }
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.innerHTML = '<span class="text-success text-sm">✓ Upload complete</span>'
    }
  }

  showError() {
    const bar = this.hasProgressBarTarget ? this.progressBarTarget : this.progressTarget?.querySelector("[data-direct-upload-target='progressBar']")
    if (bar) {
      bar.classList.remove("bg-primary", "bg-success")
      bar.classList.add("bg-error")
    }
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.innerHTML = '<span class="text-error text-sm">✗ Upload failed</span>'
    }
  }

  debug(message, type = "info") {
    if (!this.hasDebugTarget) return
    const line = document.createElement("div")
    line.className = `text-${type === "error" ? "error" : "base-content/60"} text-xs`
    line.textContent = `[${new Date().toLocaleTimeString()}] ${message}`
    this.debugTarget.insertBefore(line, this.debugTarget.firstChild)
  }

  formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return "0 B"
    const k = 1024
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${["B", "KB", "MB", "GB"][i]}`
  }

  directUploadWillStoreFileWithXHR(xhr) {
    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) this.uploadProgressing({ detail: { progress: e.loaded / e.total } })
    })
  }
}
